import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ContactSupportWidget } from "@/components/ContactSupportWidget";
import MilitaryMobileBanner from "@/components/MilitaryMobileBanner";
import TrailingSlashRedirect from "@/components/TrailingSlashRedirect";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import CanonicalUrl from "@/components/CanonicalUrl";
import AnimatedRoutes from "@/components/AnimatedRoutes";
import BackToTop from "@/components/BackToTop";
import { AuthProvider } from "./hooks/useAuth";

const queryClient = new QueryClient();

const App = () => {
  // Smooth scroll handler for anchor links (mobile-friendly)
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href*="#"]');
      
      if (!anchor) return;
      
      const href = anchor.getAttribute('href');
      if (!href || href === '#' || href === '#0') return;
      
      // Check if it's a same-page anchor link
      const url = new URL(href, window.location.origin);
      if (url.pathname !== window.location.pathname && url.pathname !== '/') return;
      
      const hash = url.hash;
      if (!hash) return;
      
      const targetElement = document.querySelector(hash) || document.querySelector(`[name="${hash.slice(1)}"]`);
      
      if (targetElement) {
        e.preventDefault();
        const navbarHeight = 70; // Account for fixed navbar
        const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - navbarHeight;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <Toaster />
              <Sonner />
              <TrailingSlashRedirect />
              <GoogleAnalytics />
              <CanonicalUrl />
              <AnimatedRoutes />
              <ContactSupportWidget />
              <BackToTop />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
